import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Check, Truck, FileText, Ban, ArrowLeft, Package, PackageCheck } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import sessionService from '../../services/sessionService';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { formatMoney } from '../../utils/money';
import '../Sessions/SessionsPage.css';
import './DemandeDetailPage.css';

const DemandeDetailPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('supplies');
  const { user, isAdmin, isLogistiqueCoordinator, isManager } = useAuth();
  const { canEdit } = usePermissions();
  const canEditDemandes = canEdit('demandes');

  const getCurrency = () => item?.supplier_devise || item?.centre_devise || user?.centres_details?.[0]?.devise || 'EUR';
  const toMoney = (value) => formatMoney(value, { locale: i18n.language, currency: getCurrency() });
  const isStockRestock = item?.order_type === 'STOCK_RESTOCK';

  const orderStatusLabel = (status) => {
    switch (status) {
      case 'PENDING_LOGISTICS':
        return t('demandes.status.PENDING_LOGISTICS');
      case 'PENDING_MANAGER':
        return t('demandes.status.PENDING_MANAGER');
      case 'NEW':
        return t('demandes.status.NEW');
      case 'ACCEPTED':
        return t('demandes.status.ACCEPTED');
      case 'SHIPPED':
        return t('demandes.status.SHIPPED');
      case 'RECEIVED':
        return t('demandes.status.RECEIVED');
      case 'INVOICED':
        return t('demandes.status.INVOICED');
      case 'CANCELLED':
        return t('demandes.status.CANCELLED');
      default:
        return status || '-';
    }
  };

  const downloadInvoice = useCallback(async () => {
    if (!item?.order_id) return;

    const filename = `purchase_order_${(item.formation_title || 'request').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    try {
      const response = await sessionService.supplierOrderInvoice(item.order_id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error(t('demandes.toasts.downloadInvoiceError'));
    }
  }, [item?.formation_title, item?.order_id, t, toast]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'PENDING_LOGISTICS':
        return 'status-pending';
      case 'PENDING_MANAGER':
        return 'status-approved';
      case 'NEW':
        return 'status-pending';
      case 'ACCEPTED':
        return 'status-approved';
      case 'SHIPPED':
        return 'status-approved';
      case 'RECEIVED':
        return 'status-approved';
      case 'INVOICED':
        return 'status-completed';
      case 'CANCELLED':
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  const allowedNextStatuses = useMemo(() => {
    const current = item?.status;
    if (isStockRestock && current === 'PENDING_LOGISTICS') {
      return (isLogistiqueCoordinator || isAdmin) ? ['PENDING_MANAGER', 'CANCELLED'] : [];
    }
    if (isStockRestock && current === 'PENDING_MANAGER') {
      return (isManager || isAdmin) ? ['NEW', 'CANCELLED'] : [];
    }

    switch (current) {
      case 'NEW':
        return ['ACCEPTED', 'CANCELLED'];
      case 'ACCEPTED':
        return ['SHIPPED', 'CANCELLED'];
      case 'SHIPPED':
        return ['RECEIVED'];
      case 'RECEIVED':
        return ['INVOICED'];
      default:
        return [];
    }
  }, [isAdmin, isLogistiqueCoordinator, isManager, isStockRestock, item?.status]);

  useEffect(() => {
    const fetchOne = async () => {
      try {
        setLoading(true);
        const data = await sessionService.supplierOrderDetail(id);
        setItem(data || null);
      } catch (e) {
        setItem(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchOne();
  }, [id]);

  const changeStatus = async (newStatus) => {
    if (!item?.order_id || !newStatus || newStatus === item.status) return;
    try {
      setStatusLoading(true);
      await sessionService.updateSupplierOrderStatus(item.order_id, newStatus);
      const refreshed = await sessionService.supplierOrderDetail(item.order_id);
      setItem(refreshed || null);
      toast.success(t('demandes.toasts.statusUpdated'));
    } catch (e) {
      toast.error(t('demandes.toasts.statusUpdateError'));
    } finally {
      setStatusLoading(false);
    }
  };

  const actionButtons = useMemo(() => {
    const map = {
      PENDING_MANAGER: { icon: Check, label: t('demandes.actions.PENDING_MANAGER') },
      NEW: { icon: Check, label: t('demandes.actions.NEW') },
      ACCEPTED: { icon: Check, label: t('demandes.actions.ACCEPTED') },
      SHIPPED: { icon: Truck, label: t('demandes.actions.SHIPPED') },
      RECEIVED: { icon: PackageCheck, label: t('demandes.actions.RECEIVED') },
      INVOICED: { icon: FileText, label: t('demandes.actions.INVOICED') },
      CANCELLED: { icon: Ban, label: t('demandes.actions.CANCELLED') },
    };

    return allowedNextStatuses.map((st) => ({
      status: st,
      Icon: map[st]?.icon,
      label: map[st]?.label || st,
    }));
  }, [allowedNextStatuses, t]);

  const topButtons = useMemo(() => {
    const buttons = canEditDemandes ? [...actionButtons] : [];
    if (!['PENDING_LOGISTICS', 'PENDING_MANAGER'].includes(item?.status)) {
      buttons.unshift({
        key: 'INVOICE_PDF',
        onClick: downloadInvoice,
        disabled: false,
        status: 'INVOICE_PDF',
        Icon: FileText,
        label: t('demandes.actions.invoicePdf'),
      });
    }
    return buttons;
  }, [actionButtons, canEditDemandes, downloadInvoice, item?.status, t]);

  const formatDate = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '-';
    return dt.toLocaleString(i18n.language);
  };

  const formatDateOnly = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '-';
    return dt.toLocaleDateString(i18n.language);
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="sessions-page">
        <div className="page-header-section">
          <div className="header-titles">
            <h1 className="page-title">
              <Calendar size={32} />
              {t('nav.demandes')}
            </h1>
          </div>
        </div>
        <div className="demande-detail-container">
          <button type="button" className="action-btn" onClick={() => navigate('/mes-demandes')}>
            <ArrowLeft size={16} />
          </button>
          <div className="demande-detail-not-found">{t('demandes.detail.notFound')}</div>
        </div>
      </div>
    );
  }

  const summaryRows = [
    item.delivery_date ? { label: t('demandes.detail.deliverBefore'), value: formatDateOnly(item.delivery_date), highlight: true } : null,
    { label: t('demandes.detail.centre'), value: item.centre_name || '-' },
    { label: t('demandes.table.type'), value: item.order_type_label || (isStockRestock ? t('demandes.orderTypes.stockRestock') : t('demandes.orderTypes.session')) },
    item.supplier_name ? { label: t('demandes.table.supplier'), value: item.supplier_name } : null,
    !isStockRestock ? { label: t('demandes.detail.trainer'), value: item.trainer_name || '-' } : null,
    { label: t('demandes.detail.createdAt'), value: formatDate(item.created_at) },
  ].filter(Boolean);

  const trackingRows = [
    { label: t('demandes.detail.createdAt'), value: formatDate(item.created_at), done: Boolean(item.created_at) },
    item.delivery_date ? { label: t('demandes.detail.deliverBefore'), value: formatDateOnly(item.delivery_date), done: true, highlight: true } : null,
    item.accepted_at ? { label: t('demandes.detail.acceptedAt'), value: formatDate(item.accepted_at) } : null,
    item.shipped_at ? { label: t('demandes.detail.shippedAt'), value: formatDate(item.shipped_at) } : null,
    item.received_at ? { label: t('demandes.detail.receivedAt'), value: formatDate(item.received_at) } : null,
    item.invoiced_at ? { label: t('demandes.detail.invoicedAt'), value: formatDate(item.invoiced_at) } : null,
  ].filter(Boolean).map((row) => ({ ...row, done: row.done ?? row.value !== '-' }));

  return (
    <div className="demande-detail-page">
      <button type="button" className="back-link demande-back-link" onClick={() => navigate('/mes-demandes')}>
        <ArrowLeft size={16} />
        {t('demandes.detail.back')}
      </button>

      <div className="demande-session-layout">
        <aside className="demande-session-info-card">
          <span className={`status-badge-large ${getStatusBadgeClass(item.status)}`}>
            {item.status_display || orderStatusLabel(item.status)}
          </span>
          <h1>{item.formation_title || t('nav.demandes')}</h1>

          {summaryRows.map((row) => (
            <div key={row.label} className={`demande-session-info-item ${row.highlight ? 'demande-delivery-highlight' : ''}`}>
              <span className="session-info-label">{row.label}</span>
              <span className="session-info-value">{row.value}</span>
            </div>
          ))}

          {topButtons.length > 0 && (
            <div className="demande-side-actions">
              {topButtons.map(({ status, Icon, label, onClick, disabled, key }) => (
                <button
                  key={key || status}
                  type="button"
                  title={label}
                  onClick={onClick || (() => changeStatus(status))}
                  disabled={disabled || statusLoading}
                  className={`btn demande-side-action-btn action-btn-${status.toLowerCase().replace('_', '-')}`}
                >
                  {Icon ? <Icon size={16} /> : null}
                  <span>{label}</span>
                </button>
              ))}
            </div>
          )}
        </aside>

        <main className="demande-main-content">
          <div className="demande-budget-hero-card">
            <div className="budget-hero-label">{t('demandes.table.quote')}</div>
            <div className="budget-hero-amount">
              <span className="amount">{item.total_devis ? toMoney(item.total_devis) : toMoney(0)}</span>
            </div>
            <div className="budget-hero-tva">
              {item.order_type_label || (isStockRestock ? t('demandes.orderTypes.stockRestock') : t('demandes.orderTypes.session'))}
              {item.supplier_name ? ` · ${item.supplier_name}` : ''}
            </div>
          </div>

          <div className="session-tabs demande-tabs">
            <button
              type="button"
              className={`session-tab ${activeTab === 'supplies' ? 'active' : ''}`}
              onClick={() => setActiveTab('supplies')}
            >
              {t('demandes.table.supplies')} ({(item.lines || []).length})
            </button>
            <button
              type="button"
              className={`session-tab ${activeTab === 'tracking' ? 'active' : ''}`}
              onClick={() => setActiveTab('tracking')}
            >
              {t('demandes.detail.tracking', { defaultValue: 'Suivi' })}
            </button>
          </div>

          {activeTab === 'supplies' && (
            (item.lines || []).length === 0 ? (
              <div className="items-list demande-empty-list">
                {t('demandes.detail.noSupplies', { defaultValue: 'Aucun consommable dans cette demande.' })}
              </div>
            ) : (
              <div className="items-list demande-items-list">
                {(item.lines || []).map((ln) => (
                  <div key={ln.id} className="item-row demande-item-row">
                    <div className="item-info">
                      <div className="item-name demande-line-name">
                        <Package size={16} />
                        <span>{ln.consommable_name}</span>
                      </div>
                      <div className="item-details">
                        {t('demandes.detail.quantityShort')}: {ln.quantity}
                        {ln.unit_price ? ` · ${toMoney(ln.unit_price)} / ${t('demandes.detail.unit')}` : ''}
                      </div>
                      {ln.calculation_detail && (
                        <div className="item-calculation demande-line-calculation">{ln.calculation_detail}</div>
                      )}
                    </div>
                    <div className="item-price">
                      {ln.line_total ? toMoney(ln.line_total) : '-'}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {activeTab === 'tracking' && (
            <div className="demande-tracking-panel">
              <div className="demande-tracking-current">
                <span className="session-info-label">{t('sessions.table.status')}</span>
                <span className={`status-badge-large ${getStatusBadgeClass(item.status)}`}>
                  {item.status_display || orderStatusLabel(item.status)}
                </span>
              </div>
              <div className="demande-tracking-list">
                {trackingRows.map((row) => (
                  <div key={row.label} className={`demande-tracking-row ${row.done ? 'is-done' : ''} ${row.highlight ? 'is-highlight' : ''}`}>
                    <span className="demande-tracking-dot" />
                    <div>
                      <span className="demande-tracking-label">{row.label}</span>
                      <strong>{row.value}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DemandeDetailPage;
